import { PreloadController, SeoController } from '@application/components';
import {
  articleTagsSelectOptions,
  generateItemsWithPromos,
  generateItemsWithPromosAmounts,
  isPromoItem,
  usePaginationSearch,
  useRedirectArticles,
  useRouter,
} from '@application/utils';
import type { ArticleListPageFormValues } from '@presentation/pages';
import { ArticleListPage } from '@presentation/pages';
import { useGetStorePromos } from '@application/api';
import { FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useArticlesWhereBuilder } from './api/useArticlesWhereBuilder';
import { useFormatArticles } from './api/useFormatArticles';
import { useGetArticles } from './api/useGetArticles';
import { useSearch } from './api/useSearch';
import { PAGINATED_SIZE } from './constants';

export const ArticleListPageController: FC = () => {
  const { translate } = useRouter();

  const { articles, articlesCount, articlesLoading, articlesRefetch } = useGetArticles();
  const { formattedArticles, imagesToPreload } = useFormatArticles(articles);
  const { articlesWhereBuilder } = useArticlesWhereBuilder();
  const { currentPage, handlePaginationChange } = usePaginationSearch();
  const { setSearchFromValues, valuesFromSearch } = useSearch();

  useRedirectArticles();

  const form = useFormik<ArticleListPageFormValues>({
    enableReinitialize: true,
    initialValues: valuesFromSearch,
    onSubmit: (values) => {
      setSearchFromValues(values);
      articlesRefetch({ where: articlesWhereBuilder(values) });
    },
  });

  const { listingPromoItems } = useGetStorePromos('articles');
  const items = useMemo(() => {
    return generateItemsWithPromos(formattedArticles ?? [], listingPromoItems, PAGINATED_SIZE);
  }, [formattedArticles, listingPromoItems]);

  return (
    <FormikProvider value={form}>
      <PreloadController images={imagesToPreload} />
      <SeoController
        title={translate('articleList.seo.title')}
        description={translate('articleList.seo.description')}
        keywords={translate('articleList.seo.keywords')}
      />
      <ArticleListPage
        loading={articlesLoading}
        items={items.list}
        isPromoItem={isPromoItem}
        pagination={{
          pageCount: articlesCount / generateItemsWithPromosAmounts(PAGINATED_SIZE).itemAmount,
          forcePage: currentPage,
          onChange: async (changedPage: number) => {
            handlePaginationChange(changedPage);
            await articlesRefetch({
              skip: generateItemsWithPromosAmounts(PAGINATED_SIZE).itemAmount * changedPage,
            });
          },
        }}
        formOptions={{
          categoryTags: articleTagsSelectOptions(translate),
        }}
      />
    </FormikProvider>
  );
};
